/// <amd-module name="Repos/FilesHubCommon/Components/FolderListing" />
import * as React from "react";
import { IMarkdownPreviewProps } from "Repos/ContentPreview/Markdown/Components/MarkdownPreview.Props";
import { IGitItem, IGitItemContent, IGitItemLastCommit } from "Repos/Files/Contracts";
import { FileDropTarget } from "Repos/FilesHubCommon/Components/FileDropTarget";
import * as Resources from "Repos/FilesHubCommon/Resources";
import { ObservableValue } from "VSS/Core/Observable";
import { ScreenBreakpoints } from "VSS/Core/Util/Screen";
import { equals, localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { PerformanceScenarioContext } from "VSS/Platform/Components/Performance";
import { IVssPageContext } from "VSS/Platform/Context";
import { css, IVssComponentContext, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { Ago } from "VSSUI/Ago";
import { Card } from "VSSUI/Card";
import { Icon } from "VSSUI/Icon";
import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { Observer } from "VSSUI/Observer";
import { ColumnMore, ColumnSorting, ITableBreakpoint, ITableColumn, ITableRow, SimpleTableCell, sortItems, SortOrder, Table } from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";
import { IFilesHubCommonService } from "../Contracts";

function onColumnResized(event: MouseEvent, index: number, width: number, column: ITableColumn<IGitItem>) {
    (column.width as ObservableValue<number>).value = width;
}

const tableBreakpoints: ITableBreakpoint[] = [
    {
        breakpoint: ScreenBreakpoints.xsmall,
        columnWidths: [160, 90, -100]
    },
    {
        breakpoint: ScreenBreakpoints.small,
        columnWidths: [250, 150, -100]
    },
    {
        breakpoint: 750,
        columnWidths: [350, 150, -100]
    }
];

function getFolderListingColumns(filesService: IFilesHubCommonService, commentsHeaderName: string): ITableColumn<IGitItem>[] {
    return [
        {
            id: "name",
            name: Resources.Name,
            onSize: onColumnResized,
            renderCell: (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<IGitItem>, tableItem: IGitItem) => {
                let content = (
                    <>
                        <Icon {...tableItem.iconProps} className={css("icon-margin font-size-ml", tableItem.iconProps.className)} />
                        
                        <FPSLink
                            href={tableItem.href}
                            className="bolt-table-link"
                            onClick={
                                (event) => {
                                    event.preventDefault();
                                    filesService.setSelectedItem(tableItem);
                                }
                            }
                            removeUnderline={true}
                        >
                            <Tooltip overflowOnly={true}>
                                <span className="text-ellipsis">{tableItem.name}</span>
                            </Tooltip>
                        </FPSLink>
                    </>
                );
                
                if (tableItem.isFolder) {
                    content = (
                        <FileDropTarget className="flex-grow flex-row" onDrop={dataTransfer => filesService.dropFiles(tableItem, dataTransfer)}>
                            {content}
                        </FileDropTarget>
                    );
                }

                return (
                    <SimpleTableCell columnIndex={columnIndex} key={columnIndex} tableColumn={tableColumn}>
                        {content}
                    </SimpleTableCell>
                );
            },
            sortProps: { ariaLabelAscending: Resources.SortByNameAsc, ariaLabelDescending: Resources.SortByNameDesc },
            width: new ObservableValue(300)
        },
        {
            id: "lastChange",
            name: Resources.LastChange,
            onSize: onColumnResized,
            renderCell: (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<IGitItem>, tableItem: IGitItem) => {
                return (
                    <SimpleTableCell columnIndex={columnIndex} key={columnIndex} tableColumn={tableColumn}>
                        <Observer commit={tableItem.lastCommit}>
                            {(props: { commit?: IGitItemLastCommit }) => {
                                return props.commit ? <Ago date={props.commit.date} /> : null;
                            }}
                        </Observer>
                    </SimpleTableCell>
                );
            },
            sortProps: { ariaLabelAscending: Resources.SortByLastChangeAsc, ariaLabelDescending: Resources.SortByLastChangeDesc },
            width: new ObservableValue(150)
        },
        {
            id: "commits",
            name: commentsHeaderName,
            renderCell: (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<IGitItem>, tableItem: IGitItem) => {
                return (
                    <Observer commit={tableItem.lastCommit} key={columnIndex}>
                        {(props: { commit?: IGitItemLastCommit }) => {
                            return (
                                <SimpleTableCell
                                    columnIndex={columnIndex}
                                    tableColumn={tableColumn}
                                    contentClassName={css(props.commit && "bolt-table-cell-content-with-link")}
                                >
                                    {props.commit && (
                                        <>
                                            <FPSLink href={props.commit.href} className="repos-folder-list-commitId bolt-table-link monospaced-text">
                                                {props.commit.shortCommitId}
                                            </FPSLink>
                                            <Tooltip overflowOnly={true} text={props.commit.comment + " - " + props.commit.name}>
                                                <span className="text-ellipsis">
                                                    <span className="repos-folder-list-comment">{props.commit.comment}</span>
                                                    &nbsp;
                                                    <span className="secondary-text">{props.commit.name}</span>
                                                </span>
                                            </Tooltip>
                                        </>
                                    )}
                                </SimpleTableCell>
                            );
                        }}
                    </Observer>
                );
            },
            width: new ObservableValue(-100)
        },
        new ColumnMore(item => {
            return {
                id: "list-sub-menu",
                items: filesService.getItemMenuItems(item, ["ms.vss-code-web.source-item-menu", "ms.vss-code-web.source-grid-item-menu"])
            };
        })
    ];
}

const folderListingSortFunctions = [
    // Sort on Name column
    (i1: IGitItem, i2: IGitItem): number => {
        return i1.isFolder === i2.isFolder ? localeIgnoreCaseComparer(i1.name, i2.name) : i1.isFolder ? -1 : 1;
    },
    // Sort on last-commit date
    (i1: IGitItem, i2: IGitItem): number => {
        return (i1.lastCommit.value ? i1.lastCommit.value.date.getTime() : 0) - (i2.lastCommit.value ? i2.lastCommit.value.date.getTime() : 0);
    }
];

// Use 3 loading rows for the folder listing table
const folderListingWaitItems = [new ObservableValue(undefined), new ObservableValue(undefined), new ObservableValue(undefined)];

interface IFolderListingProps {
    commentsHeaderTitle: string;
    filesService: IFilesHubCommonService;
}

export class FolderListing extends VssComponent<IFolderListingProps> {
    private columns: ITableColumn<IGitItem>[];
    private sortOptions = new ObservableValue({ columnIndex: 0, order: SortOrder.ascending });

    private sortingBehavior = new ColumnSorting<IGitItem>((columnIndex: number, order: SortOrder) => {
        this.sortOptions.value = { columnIndex, order };
    });

    constructor(props: IFolderListingProps, context: IVssComponentContext) {
        super(props, context);

        this.columns = getFolderListingColumns(props.filesService, props.commentsHeaderTitle);
    }

    public render() {
        const { filesService } = this.props;
        const folder = filesService.getSelectedItem().value;
        const childItems = filesService.getChildItems(folder.path, true);

        return (
            <PerformanceScenarioContext.Consumer>
                {scenarioProps => {
                    const pendingWork = scenarioProps.registerPendingWork("load-folder-list");
                    return (
                        <Observer ready={childItems.ready} items={childItems}>
                            {(readyProps: { ready: boolean; items: IGitItem[] }) => {
                                const { ready, items } = readyProps;
                                return (
                                    <>
                                        <Card className="bolt-table-card">
                                            <FileDropTarget onDrop={dataTransfer => filesService.dropFiles(folder, dataTransfer)}>
                                                <Observer sort={this.sortOptions}>
                                                    {(sortProps: { sort: { columnIndex: number; order: SortOrder } }) => {
                                                        return (
                                                            <FolderTable
                                                                columns={this.columns}
                                                                items={items}
                                                                filesService={this.props.filesService}
                                                                onLoaded={pendingWork && pendingWork.complete}
                                                                parentPath={folder.path}
                                                                ready={ready}
                                                                sort={sortProps.sort}
                                                                sortBehavior={this.sortingBehavior}
                                                            />
                                                        );
                                                    }}
                                                </Observer>
                                            </FileDropTarget>
                                        </Card>
                                        <FolderReadme filesService={filesService} items={items} pageContext={this.context.pageContext} />
                                    </>
                                );
                            }}
                        </Observer>
                    );
                }}
            </PerformanceScenarioContext.Consumer>
        );
    }
}

interface IFolderTableProps {
    columns: ITableColumn<IGitItem>[];
    filesService: IFilesHubCommonService;
    items: IGitItem[];
    onLoaded?: () => void;
    parentPath: string;
    ready: boolean;
    sort: { columnIndex: number; order: SortOrder };
    sortBehavior: ColumnSorting<IGitItem>;
}

const FolderTable = (props: IFolderTableProps): JSX.Element => {
    const { columns, items, onLoaded, ready, sort, sortBehavior } = props;

    const onActivate = (event: React.SyntheticEvent<HTMLElement>, tableRow: ITableRow<IGitItem>): void => {
        if (tableRow.data) {
            props.filesService.setSelectedItem(tableRow.data);
            event.preventDefault();
        }
    };

    React.useEffect(() => {
        if (ready) {
            onLoaded && onLoaded();
        }
    });

    const tableItems = ready
        ? new ArrayItemProvider(sortItems(sort.columnIndex, sort.order, folderListingSortFunctions, columns, [...items]))
        : new ArrayItemProvider(folderListingWaitItems);

    return (
        <Table
            ariaLabel={Resources.FilesTable}
            behaviors={[sortBehavior]}
            columns={columns}
            itemProvider={tableItems}
            onActivate={onActivate}
            role="treegrid"
            selectableText={true}
            showHeader={true}
            tableBreakpoints={tableBreakpoints}
        />
    );
};

interface IFolderReadmeProps {
    filesService: IFilesHubCommonService;
    items: IGitItem[];
    pageContext: IVssPageContext;
}

const FolderReadme = (props: IFolderReadmeProps): JSX.Element | null => {
    const { filesService, items } = props;

    const readmeFile = items.filter(i => equals(i.name, "Readme.md", true))[0];
    if (!readmeFile) {
        return null;
    }

    const urlTransformer = filesService.getUrlTransformer(readmeFile.path);
    return (
        <Observer content={filesService.getItemContent(readmeFile.path)}>
            {(contentProps: { content: IGitItemContent | undefined }) => {
                let readmeContent = "";
                if (contentProps.content) {
                    readmeContent = contentProps.content.details.content;
                }

                return (
                    <Card className="no-padding repos-folder-list-readme-card" contentProps={{ contentPadding: false }}>
                        {readmeContent ? (
                            <WrappedComponent<IMarkdownPreviewProps>
                                wrappedType="ms.vss-code-web.markdown-preview"
                                dependencies={["ms.vss-code-web.markdown-preview-content", "ms.vss-features.markdown-highlight-service"]}
                                wrappedProps={{
                                    className: "flex-grow",
                                    linkTransformer: urlTransformer.transformLink,
                                    imageUrlTransformer: urlTransformer.transformImage,
                                    linkParse: urlTransformer.parseFilesHubUrl,
                                    rawContent: readmeContent
                                }}
                            />
                        ) : null}
                    </Card>
                );
            }}
        </Observer>
    );
};
